import React, { useRef, useState, useEffect } from "react";
import { FaPlay } from "react-icons/fa";
import { FaPause } from "react-icons/fa";
import { GoUnmute } from "react-icons/go";
import { GoMute } from "react-icons/go";
import { RiFullscreenFill } from "react-icons/ri";
import { RiFullscreenExitFill } from "react-icons/ri";
import { motion } from "framer-motion";

const Video = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [justStarted, setJustStarted] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
    };

    video.onloadedmetadata = () => setDuration(video.duration);
    video.ontimeupdate = updateTime;
    video.onplay = () => {
      setIsPlaying(true);
      setHasStarted(true);
      setJustStarted(true);
      setTimeout(() => setJustStarted(false), 100);
    };
    video.onpause = () => setIsPlaying(false);
    video.onended = () => setIsPlaying(false);
  }, []);

  const togglePlay = (e) => {
    e?.stopPropagation();
    const video = videoRef.current;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleVideoClick = (e) => {
    if (e.target === videoRef.current || e.target.tagName === "VIDEO") {
      togglePlay();
    }
  };

  const handleProgressClick = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    video.currentTime = pos * video.duration;
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    setVolume(video.muted ? 0 : video.volume);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = (e) => {
    e.stopPropagation();

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isInitialState = !hasStarted && !isPlaying;

  const effectiveHover = isHovering && !justStarted;
  const showOverlayAndControls = effectiveHover || (!isPlaying && hasStarted);

  return (
    <div className="py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 2, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <h2
            style={{ color: "white" }}
            className="text-3xl font-semibold text-center mx-auto mb-10"
          >
            This is a Powerful Proactive Tool for Any Modern Data Team
          </h2>

          <div
            ref={containerRef}
            className="relative w-full xl:w-2/3 mx-auto rounded-3xl overflow-hidden cursor-pointer shadow-md"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <video
              ref={videoRef}
              src={"/DATAGENX-Demo.mp4"}
              poster={"/poster.png"}
              className="w-full h-auto block"
              onClick={handleVideoClick}
              playsInline
            />

            {isInitialState && (
              <>
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    top: 0,
                    left: 0,
                    zIndex: 19,
                  }}
                ></div>{" "}
                <button
                  onClick={togglePlay}
                  className="
              absolute top-1/2 left-1/2
              -translate-x-1/2 -translate-y-1/2
              aspect-square
             text-white
              text-[clamp(2rem,5vw,3rem)]
              rounded-full
              flex items-center justify-center
              transition-all duration-300
              z-20
              cursor-pointer
            "
                  style={{
                    background:
                      "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
                    width: "50px",
                    height: "50px",
                    transform: "translate(-50%, -50%)",
                    boxShadow: `
                  0 12px 25px rgba(69, 42, 209, 0.35),
                  0 8px 20px rgba(113, 93, 218, 0.30),
                  0 4px 15px rgba(62, 223, 208, 0.25)`,
                  }}
                >
                  <FaPlay
                    className="
                  text-2xl text-white
                "
                    style={{ marginLeft: "4px" }}
                  />
                </button>
              </>
            )}

            {!isInitialState && (
              <>
                {showOverlayAndControls && (
                  <div className="absolute inset-0 bg-black/40 pointer-events-none z-10 transition-opacity duration-300" />
                )}

                <button
                  onClick={togglePlay}
                  className={`
                  absolute top-1/2 left-1/2
                  -translate-x-1/2 -translate-y-1/2
                  aspect-square
                text-white
                  text-[clamp(2rem,5vw,3rem)]
                  rounded-full
                  flex items-center justify-center
                  transition-all duration-300
                  z-20
                  cursor-pointer
                  ${
                    showOverlayAndControls ? "opacity-100" : "opacity-0"
                  } cursor-pointer`}
                  style={{
                    background:
                      "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
                    width: "50px",
                    height: "50px",
                    transform: "translate(-50%, -50%)",
                    boxShadow: `
                    0 12px 25px rgba(69, 42, 209, 0.35),
                    0 8px 20px rgba(113, 93, 218, 0.30),
                    0 4px 15px rgba(62, 223, 208, 0.25)
                  `,
                  }}
                >
                  {isPlaying ? (
                    <FaPause className="text-2xl text-white" />
                  ) : (
                    <FaPlay
                      className="
                      text-2xl text-white
                    "
                      style={{ marginLeft: "4px" }}
                    />
                  )}
                </button>

                <div
                  className={`absolute bottom-0 left-0 right-0 pt-2 px-5 flex items-center gap-3 md:gap-4 z-20 transition-opacity duration-300 ${
                    showOverlayAndControls ? "opacity-100" : "opacity-0"
                  }`}
                  dir="ltr"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 70%, transparent 100%)",
                    height: "15%",
                  }}
                >
                  <span className="text-black text-xs md:text-sm font-medium cursor-default">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>

                  <div
                    onClick={handleProgressClick}
                    className="flex-1 h-2 rounded-full cursor-pointer relative group"
                    style={{ backgroundColor: "rgba(113, 93, 218, 0.25)" }}
                  >
                    <div
                      className="
                      absolute top-0 left-0 h-full rounded-full
                      transition-all duration-200
                    "
                      style={{
                        width: `${progress}%`,
                        background:
                          "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
                        boxShadow: `
                        0 12px 25px rgba(69, 42, 209, 0.35),
                        0 8px 20px rgba(113, 93, 218, 0.30),
                        0 4px 15px rgba(62, 223, 208, 0.25)
                      `,
                      }}
                    />

                    <div
                      className="
                      absolute top-1/2 -translate-y-1/2
                      w-3 h-3 md:w-4 md:h-4
                      rounded-full
                      -translate-x-1/2
                      transition-all duration-200
                      group-hover:scale-110
                    "
                      style={{
                        transform: "translate(-50%, -50%)",
                        left: `${progress}%`,
                        background:
                          "linear-gradient(135deg, #452ad1, #715dda, #3edfd0)",
                        boxShadow: `
                        0 12px 25px rgba(69, 42, 209, 0.35),
                        0 8px 20px rgba(113, 93, 218, 0.30),
                        0 4px 15px rgba(62, 223, 208, 0.25)
                      `,
                      }}
                    />
                  </div>

                  <div onClick={toggleMute} className="text-2xl text-black">
                    {isMuted ? <GoMute /> : <GoUnmute />}
                  </div>

                  <div
                    onClick={toggleFullscreen}
                    className="text-black text-2xl"
                  >
                    {isFullScreen ? (
                      <RiFullscreenExitFill />
                    ) : (
                      <RiFullscreenFill />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Video;
